import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { CalendarCheck2, House, Menu, MessagesSquare, MessageSquarePlus, Search } from "lucide-react";
import {
  ApiError,
  cancelRun,
  consumeCodexReset,
  createProject,
  createSession,
  deleteSession,
  getAuthStatus,
  getBootstrap,
  getCapabilities,
  getConfiguration,
  getStatus,
  getSession,
  login,
  logout,
  resolveApproval,
  retryMessage,
  sendMessage,
  sendInteractionResponse,
  updateCapabilityPolicy,
  updateCodexResetPolicy,
  updateSessionSettings,
  updateSettings,
  updateTheme as updateThemeSetting,
} from "./api";
import { AuthGate } from "./components/AuthGate";
import { ChatView } from "./components/ChatView";
import { ConversationContextBar } from "./components/ConversationContextBar";
import { CreateProjectDialog } from "./components/CreateProjectDialog";
import { CreateSessionDialog } from "./components/CreateSessionDialog";
import type { FileExplorerTarget } from "./components/FileExplorerPanel";
import { GlobalDrawer } from "./components/GlobalDrawer";
import { SessionRail } from "./components/SessionRail";
import { StatusBar } from "./components/StatusBar";
import { ToastViewport } from "./components/ToastViewport";
import type {
  AgentSelection,
  ApprovalDecision,
  BootstrapPayload,
  CapabilityInventory,
  CapabilityApprovalMode,
  ConfigurationPayload,
  EventRecord,
  InteractionAnswerInput,
  NoraSettings,
  SessionDetail,
  SessionSummary,
  StatusPayload,
  ThemeId,
  ToastNotice,
  ToastTone,
} from "./types";
import { approvalDecisionNotice } from "./components/ApprovalPrompt";
import { applyTheme, getTheme, readStoredTheme } from "./theme";
import { installFileOpenGestureGuard, shouldSuppressFileOpen } from "./fileOpenGestureGuard";

const MemoryPanel = lazy(() => import("./components/MemoryPanel").then((module) => ({ default: module.MemoryPanel })));
const RecurringTasksPanel = lazy(() => import("./components/RecurringTasksPanel").then((module) => ({ default: module.RecurringTasksPanel })));
const TodayPanel = lazy(() => import("./components/TodayPanel").then((module) => ({ default: module.TodayPanel })));
const SearchPanel = lazy(() => import("./components/SearchPanel").then((module) => ({ default: module.SearchPanel })));
const ArtifactsPanel = lazy(() => import("./components/ArtifactsPanel").then((module) => ({ default: module.ArtifactsPanel })));
const ProjectsPanel = lazy(() => import("./components/ProjectsPanel").then((module) => ({ default: module.ProjectsPanel })));
const ProjectDetailPanel = lazy(() => import("./components/ProjectDetailPanel").then((module) => ({ default: module.ProjectDetailPanel })));
const FileExplorerPanel = lazy(() => import("./components/FileExplorerPanel").then((module) => ({ default: module.FileExplorerPanel })));
const CapabilitiesPanel = lazy(() => import("./components/CapabilitiesPanel").then((module) => ({ default: module.CapabilitiesPanel })));
const ConfigurationPanel = lazy(() => import("./components/ConfigurationPanel").then((module) => ({ default: module.ConfigurationPanel })));
const UsagePanel = lazy(() => import("./components/UsagePanel").then((module) => ({ default: module.UsagePanel })));

type AuthState = "checking" | "required" | "authenticated";

export default function App() {
  const [authState, setAuthState] = useState<AuthState>("checking");
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");
  const [themeId, setThemeId] = useState<ThemeId>(() => readStoredTheme());
  const [bootstrap, setBootstrap] = useState<BootstrapPayload | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(() => localStorage.getItem("nora.activeSessionId"));
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [streamConnected, setStreamConnected] = useState(false);
  const [status, setStatus] = useState<StatusPayload | null>(null);
  const [sessionRailOpen, setSessionRailOpen] = useState(false);
  const [globalDrawerOpen, setGlobalDrawerOpen] = useState(false);
  const [todayOpen, setTodayOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [artifactsOpen, setArtifactsOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const [fileTarget, setFileTarget] = useState<FileExplorerTarget | null>(null);
  const [memoryProjectFilterId, setMemoryProjectFilterId] = useState<string | null>(null);
  const [capabilitiesOpen, setCapabilitiesOpen] = useState(false);
  const [capabilities, setCapabilities] = useState<CapabilityInventory | null>(null);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [capabilitiesError, setCapabilitiesError] = useState("");
  const [capabilitiesProjectId, setCapabilitiesProjectId] = useState<string | null>(null);
  const [capabilityPolicyBusyId, setCapabilityPolicyBusyId] = useState<string | null>(null);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [recurringTasksOpen, setRecurringTasksOpen] = useState(false);
  const [projectDetailId, setProjectDetailId] = useState<string | null>(null);
  const [sessionProjectFilterId, setSessionProjectFilterId] = useState<string | null>(null);
  const [configurationOpen, setConfigurationOpen] = useState(false);
  const [configuration, setConfiguration] = useState<ConfigurationPayload | null>(null);
  const [configurationLoading, setConfigurationLoading] = useState(false);
  const [configurationBusy, setConfigurationBusy] = useState(false);
  const [configurationError, setConfigurationError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createSessionProjectId, setCreateSessionProjectId] = useState<string | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [createProjectBusy, setCreateProjectBusy] = useState(false);
  const [createProjectError, setCreateProjectError] = useState("");
  const [sending, setSending] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
  const [notices, setNotices] = useState<ToastNotice[]>([]);
  const activeSessionRef = useRef(activeSessionId);
  const detailRef = useRef(detail);
  const refreshTimer = useRef<number | null>(null);
  const noticeSequence = useRef(0);
  const noticeTimers = useRef(new Map<number, number>());

  const dismissNotice = useCallback((id: number) => {
    const timer = noticeTimers.current.get(id);
    if (timer != null) window.clearTimeout(timer);
    noticeTimers.current.delete(id);
    setNotices((current) => current.filter((notice) => notice.id !== id));
  }, []);

  const showNotice = useCallback((message: string, tone: ToastTone = "error") => {
    const id = ++noticeSequence.current;
    setNotices((current) => [...current, { id, message, tone }].slice(-4));
    const timer = window.setTimeout(() => dismissNotice(id), tone === "error" ? 6500 : 4200);
    noticeTimers.current.set(id, timer);
    for (const [noticeId, pendingTimer] of noticeTimers.current) {
      if (noticeId >= id - 3) continue;
      window.clearTimeout(pendingTimer);
      noticeTimers.current.delete(noticeId);
    }
  }, [dismissNotice]);

  const closeFiles = useCallback(() => {
    setFilesOpen(false);
    setFileTarget(null);
  }, []);

  useEffect(() => { activeSessionRef.current = activeSessionId; }, [activeSessionId]);
  useEffect(() => { detailRef.current = detail; }, [detail]);
  useEffect(() => { applyTheme(themeId); }, [themeId]);
  useEffect(() => installFileOpenGestureGuard(), []);
  useEffect(() => {
    closeFiles();
  }, [activeSessionId, closeFiles]);
  useEffect(() => () => {
    for (const timer of noticeTimers.current.values()) window.clearTimeout(timer);
    noticeTimers.current.clear();
  }, []);

  const refreshBootstrap = useCallback(async () => {
    const payload = await getBootstrap();
    setBootstrap(payload);
    setThemeId(payload.settings.themeId);
    setActiveSessionId((current) => {
      const next = current && payload.sessions.some((session) => session.id === current)
        ? current
        : payload.sessions[0]?.id ?? null;
      activeSessionRef.current = next;
      if (next) localStorage.setItem("nora.activeSessionId", next);
      else localStorage.removeItem("nora.activeSessionId");
      return next;
    });
    return payload;
  }, []);

  const refreshDetail = useCallback(async (sessionId: string | null = activeSessionRef.current) => {
    if (!sessionId) {
      detailRef.current = null;
      setDetail(null);
      return;
    }
    try {
      const next = await getSession(sessionId);
      detailRef.current = next;
      setDetail(next);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        await refreshBootstrap();
        return;
      }
      throw error;
    }
  }, [refreshBootstrap]);

  useEffect(() => {
    void getAuthStatus().then(async (authenticated) => {
      if (!authenticated) {
        setAuthState("required");
        return;
      }
      await refreshBootstrap();
      setAuthState("authenticated");
    }).catch(() => setAuthState("required"));
  }, [refreshBootstrap]);

  useEffect(() => {
    if (authState !== "authenticated" || !activeSessionId) {
      setDetailLoading(false);
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    if (detailRef.current?.session.id !== activeSessionId) {
      detailRef.current = null;
      setDetail(null);
    }
    void refreshDetail(activeSessionId)
      .catch((error) => showNotice(`Chargement de la conversation impossible : ${errorMessage(error)}`))
      .finally(() => setDetailLoading(false));
  }, [activeSessionId, authState, refreshDetail, showNotice]);

  useEffect(() => {
    if (authState !== "authenticated" || !bootstrap) return;
    const stored = Number(localStorage.getItem("nora.eventCursor") ?? bootstrap.latestSeq);
    const after = Number.isFinite(stored) && stored <= bootstrap.latestSeq ? stored : bootstrap.latestSeq;
    localStorage.setItem("nora.eventCursor", String(after));
    const source = new EventSource(`/api/events?after=${after}`);
    source.onopen = () => setStreamConnected(true);
    source.onerror = () => setStreamConnected(false);
    source.onmessage = (message) => {
      let event: EventRecord;
      try { event = JSON.parse(message.data) as EventRecord; }
      catch { return; }
      localStorage.setItem("nora.eventCursor", String(event.seq));
      const notification = recurringTaskNotification(event);
      if (notification) showNotice(notification.message, notification.tone);
      if (event.type === "approval.requested") {
        showNotice(
          event.sessionId === activeSessionRef.current
            ? "Une validation est requise pour continuer cette réponse."
            : "Une validation est requise dans une autre conversation.",
          "info"
        );
      }
      if (
        event.type === "project.request.routed"
        && event.sessionId === activeSessionRef.current
        && event.payload.autoOpen === true
        && typeof event.payload.targetSessionId === "string"
      ) {
        const targetSessionId = event.payload.targetSessionId;
        const projectName = typeof event.payload.projectName === "string"
          ? event.payload.projectName
          : "le projet";
        localStorage.setItem("nora.activeSessionId", targetSessionId);
        activeSessionRef.current = targetSessionId;
        detailRef.current = null;
        setDetail(null);
        setDetailLoading(true);
        setActiveSessionId(targetSessionId);
        showNotice(`Suite lancée dans « ${projectName} ».`, "info");
        void refreshBootstrap().catch((error) => {
          showNotice(`Le projet est ouvert, mais l’index n’a pas pu être actualisé : ${errorMessage(error)}`);
        });
        return;
      }
      if (event.type !== "assistant.updated" && event.sessionId === activeSessionRef.current) {
        const next = applyLiveEvent(detailRef.current, event);
        if (next) {
          detailRef.current = next;
          setDetail(next);
        }
      }
      if (event.type === "assistant.updated" && event.sessionId === activeSessionRef.current) {
        const next = applyAssistantUpdate(detailRef.current, event);
        if (next) {
          detailRef.current = next;
          setDetail(next);
          return;
        }
        if (refreshTimer.current != null) window.clearTimeout(refreshTimer.current);
        refreshTimer.current = window.setTimeout(() => {
          refreshTimer.current = null;
          void refreshDetail(event.sessionId).catch((error) => {
            showNotice(`Actualisation de la conversation impossible : ${errorMessage(error)}`);
          });
        }, 100);
        return;
      }
      const refreshBootstrapNeeded = shouldRefreshBootstrapForEvent(event.type);
      const refreshDetailNeeded = event.sessionId === activeSessionRef.current
        && shouldRefreshDetailForEvent(event.type);
      if (!refreshBootstrapNeeded && !refreshDetailNeeded) return;
      if (refreshTimer.current != null) window.clearTimeout(refreshTimer.current);
      refreshTimer.current = window.setTimeout(() => {
        refreshTimer.current = null;
        void Promise.all([
          refreshBootstrapNeeded ? refreshBootstrap() : Promise.resolve(),
          refreshDetailNeeded ? refreshDetail(event.sessionId) : Promise.resolve(),
        ]).catch((error) => {
          showNotice(`Actualisation de Nora impossible : ${errorMessage(error)}`);
        });
      }, 140);
    };
    return () => {
      source.close();
      if (refreshTimer.current != null) {
        window.clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
    };
  }, [authState, Boolean(bootstrap), refreshBootstrap, refreshDetail, showNotice]);

  useEffect(() => {
    if (authState !== "authenticated") return;
    const poll = () => void getStatus().then(setStatus).catch(() => setStatus(null));
    poll();
    const timer = window.setInterval(poll, 15_000);
    return () => window.clearInterval(timer);
  }, [authState]);

  async function handleLogin(token: string) {
    setAuthBusy(true);
    setAuthError("");
    try {
      await login(token);
      await refreshBootstrap();
      setAuthState("authenticated");
    } catch (error) {
      setAuthError(errorMessage(error));
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleLogout() {
    await logout();
    setConfigurationOpen(false);
    setGlobalDrawerOpen(false);
    setTodayOpen(false);
    setSearchOpen(false);
    setArtifactsOpen(false);
    setSessionRailOpen(false);
    setUsageOpen(false);
    setMemoryOpen(false);
    closeFiles();
    setCapabilitiesOpen(false);
    setProjectsOpen(false);
    setRecurringTasksOpen(false);
    setProjectDetailId(null);
    setAuthState("required");
    setBootstrap(null);
    setDetail(null);
    setStatus(null);
    setNotices([]);
    for (const timer of noticeTimers.current.values()) window.clearTimeout(timer);
    noticeTimers.current.clear();
  }

  async function loadConfiguration(refresh = false) {
    setConfigurationLoading(true);
    setConfigurationError("");
    try {
      setConfiguration(await getConfiguration(refresh));
    } catch (error) {
      const message = errorMessage(error);
      setConfigurationError(message);
      showNotice(message);
    } finally {
      setConfigurationLoading(false);
    }
  }

  function openConfiguration() {
    setTodayOpen(false);
    setSearchOpen(false);
    setArtifactsOpen(false);
    setGlobalDrawerOpen(false);
    setSessionRailOpen(false);
    setUsageOpen(false);
    setMemoryOpen(false);
    closeFiles();
    setCapabilitiesOpen(false);
    setProjectsOpen(false);
    setRecurringTasksOpen(false);
    setProjectDetailId(null);
    setConfigurationOpen(true);
    void loadConfiguration(false);
  }

  function openSessions(projectId: string | null = null) {
    setTodayOpen(false);
    setSearchOpen(false);
    setArtifactsOpen(false);
    setGlobalDrawerOpen(false);
    setUsageOpen(false);
    closeFiles();
    setProjectsOpen(false);
    setRecurringTasksOpen(false);
    setCapabilitiesOpen(false);
    setProjectDetailId(null);
    setSessionProjectFilterId(projectId);
    setSessionRailOpen(true);
  }

  function openUsage() {
    setTodayOpen(false);
    setSearchOpen(false);
    setArtifactsOpen(false);
    setGlobalDrawerOpen(false);
    setSessionRailOpen(false);
    setProjectsOpen(false);
    setRecurringTasksOpen(false);
    setConfigurationOpen(false);
    setMemoryOpen(false);
    closeFiles();
    setCapabilitiesOpen(false);
    setProjectDetailId(null);
    setUsageOpen(true);
  }

  async function saveCodexResetPolicy(enabled: boolean, thresholdPercent: number) {
    setResetBusy(true);
    try {
      const result = await updateCodexResetPolicy(enabled, thresholdPercent);
      setStatus((current) => current ? {
        ...current,
        resetPolicy: result.resetPolicy,
        quotas: result.automaticResult?.quotas ?? current.quotas,
      } : current);
      showNotice(
        enabled
          ? `Reset automatique actif à ${thresholdPercent} % restant.`
          : "Reset automatique désactivé.",
        "success"
      );
    } catch (error) {
      showNotice(errorMessage(error));
    } finally {
      setResetBusy(false);
    }
  }

  async function useCodexReset() {
    setResetBusy(true);
    try {
      const result = await consumeCodexReset();
      setStatus((current) => current ? {
        ...current,
        quotas: result.quotas ?? current.quotas,
      } : current);
      const messages: Record<string, string> = {
        reset: "Réinitialisation Codex appliquée.",
        alreadyRedeemed: "Cette réinitialisation avait déjà été appliquée.",
        nothingToReset: "Aucune fenêtre Codex n’est encore éligible.",
        noCredit: "Aucun crédit de réinitialisation n’est disponible.",
      };
      showNotice(
        messages[result.outcome] ?? "État des réinitialisations actualisé.",
        result.outcome === "reset" || result.outcome === "alreadyRedeemed" ? "success" : "info"
      );
    } catch (error) {
      showNotice(errorMessage(error));
    } finally {
      setResetBusy(false);
    }
  }

  function openMemory(projectId: string | null = null) {
    setTodayOpen(false);
    setSearchOpen(false);
    setArtifactsOpen(false);
    setGlobalDrawerOpen(false);
    setSessionRailOpen(false);
    setUsageOpen(false);
    setProjectsOpen(false);
    setRecurringTasksOpen(false);
    closeFiles();
    setCapabilitiesOpen(false);
    setProjectDetailId(null);
    setConfigurationOpen(false);
    setMemoryProjectFilterId(projectId);
    setMemoryOpen(true);
  }

  function openProjects() {
    setTodayOpen(false);
    setSearchOpen(false);
    setArtifactsOpen(false);
    setGlobalDrawerOpen(false);
    setSessionRailOpen(false);
    setUsageOpen(false);
    setMemoryOpen(false);
    closeFiles();
    setCapabilitiesOpen(false);
    setRecurringTasksOpen(false);
    setConfigurationOpen(false);
    setProjectDetailId(null);
    setProjectsOpen(true);
  }

  function openRecurringTasks() {
    setTodayOpen(false);
    setSearchOpen(false);
    setArtifactsOpen(false);
    setGlobalDrawerOpen(false);
    setSessionRailOpen(false);
    setUsageOpen(false);
    setMemoryOpen(false);
    closeFiles();
    setCapabilitiesOpen(false);
    setProjectsOpen(false);
    setProjectDetailId(null);
    setConfigurationOpen(false);
    setRecurringTasksOpen(true);
  }

  function openProject(projectId: string) {
    setTodayOpen(false);
    setSearchOpen(false);
    setArtifactsOpen(false);
    setGlobalDrawerOpen(false);
    setSessionRailOpen(false);
    setUsageOpen(false);
    setMemoryOpen(false);
    closeFiles();
    setCapabilitiesOpen(false);
    setConfigurationOpen(false);
    setProjectsOpen(false);
    setRecurringTasksOpen(false);
    setProjectDetailId(projectId);
  }

  function openGlobalDrawer() {
    setTodayOpen(false);
    setSearchOpen(false);
    setArtifactsOpen(false);
    setSessionRailOpen(false);
    setUsageOpen(false);
    setMemoryOpen(false);
    closeFiles();
    setCapabilitiesOpen(false);
    setProjectsOpen(false);
    setRecurringTasksOpen(false);
    setProjectDetailId(null);
    setGlobalDrawerOpen(true);
  }

  function openToday() {
    closePrimaryPanels();
    setTodayOpen(true);
  }

  function openSearch() {
    closePrimaryPanels();
    setSearchOpen(true);
  }

  function openArtifacts() {
    closePrimaryPanels();
    setArtifactsOpen(true);
  }

  function closePrimaryPanels() {
    setGlobalDrawerOpen(false);
    setSessionRailOpen(false);
    setUsageOpen(false);
    setMemoryOpen(false);
    closeFiles();
    setCapabilitiesOpen(false);
    setProjectsOpen(false);
    setRecurringTasksOpen(false);
    setConfigurationOpen(false);
    setProjectDetailId(null);
    setTodayOpen(false);
    setSearchOpen(false);
    setArtifactsOpen(false);
  }

  async function loadCapabilities(
    refresh = false,
    projectId: string | null = capabilitiesProjectId
  ) {
    setCapabilitiesLoading(true);
    setCapabilitiesError("");
    try {
      setCapabilities(await getCapabilities(refresh, projectId));
    } catch (error) {
      const message = errorMessage(error);
      setCapabilitiesError(message);
      showNotice(message);
    } finally {
      setCapabilitiesLoading(false);
    }
  }

  function openCapabilities() {
    setTodayOpen(false);
    setSearchOpen(false);
    setArtifactsOpen(false);
    setGlobalDrawerOpen(false);
    setSessionRailOpen(false);
    setUsageOpen(false);
    setMemoryOpen(false);
    closeFiles();
    setProjectsOpen(false);
    setRecurringTasksOpen(false);
    setProjectDetailId(null);
    setConfigurationOpen(false);
    setCapabilitiesOpen(true);
    void loadCapabilities(true);
  }

  async function selectCapabilitiesProject(projectId: string | null) {
    setCapabilitiesProjectId(projectId);
    await loadCapabilities(false, projectId);
  }

  async function saveCapabilityPolicy(input: {
    capabilityId: string;
    projectId: string | null;
    enabled: boolean | null;
    approvalMode: CapabilityApprovalMode | null;
  }) {
    setCapabilityPolicyBusyId(input.capabilityId);
    setCapabilitiesError("");
    try {
      await updateCapabilityPolicy(input.capabilityId, input);
      await loadCapabilities(true, input.projectId);
      showNotice(input.projectId ? "Règle du projet enregistrée." : "Règle globale enregistrée.", "success");
    } catch (error) {
      const message = errorMessage(error);
      setCapabilitiesError(message);
      showNotice(message);
    } finally {
      setCapabilityPolicyBusyId(null);
    }
  }

  async function askNoraToAddCapability() {
    setCapabilitiesOpen(false);
    await handleSend(
      "Je veux ajouter ou connecter une nouvelle capacité à Nora. Commence par inventorier l’existant, puis aide-moi à choisir entre un skill standard, un MCP ou une API encapsulée en MCP. Automatise ce qui est sûr et demande-moi uniquement les informations ou confirmations indispensables."
    );
  }

  const openFiles = useCallback((
    path: string,
    line: number | null = null,
    kind: FileExplorerTarget["kind"] = "auto",
    sourceSessionId?: string
  ) => {
    if (sourceSessionId !== undefined && sourceSessionId !== activeSessionRef.current) return;
    if (shouldSuppressFileOpen()) return;
    setTodayOpen(false);
    setSearchOpen(false);
    setArtifactsOpen(false);
    setGlobalDrawerOpen(false);
    setSessionRailOpen(false);
    setUsageOpen(false);
    setMemoryOpen(false);
    setCapabilitiesOpen(false);
    setProjectsOpen(false);
    setRecurringTasksOpen(false);
    setProjectDetailId(null);
    setConfigurationOpen(false);
    setFileTarget({ path, line, kind, requestId: Date.now() });
    setFilesOpen(true);
  }, []);

  function openCreateSession(projectId: string | null = null) {
    setCreateError("");
    setCreateSessionProjectId(projectId);
    setCreateOpen(true);
  }

  async function handleSaveDefaults(input: Omit<NoraSettings, "updatedAt">) {
    setConfigurationBusy(true);
    setConfigurationError("");
    try {
      const settings = await updateSettings(input);
      setConfiguration((current) => current ? { ...current, settings } : current);
      await refreshBootstrap();
      showNotice("Configuration enregistrée.", "success");
    } catch (error) {
      const message = errorMessage(error);
      setConfigurationError(message);
      showNotice(message);
    } finally {
      setConfigurationBusy(false);
    }
  }

  async function handleSaveSessionSettings(input: AgentSelection) {
    if (!activeSessionId) return;
    setConfigurationBusy(true);
    setConfigurationError("");
    try {
      await updateSessionSettings(activeSessionId, input);
      await Promise.all([refreshBootstrap(), refreshDetail(activeSessionId)]);
      showNotice("Réglages de la session enregistrés.", "success");
    } catch (error) {
      const message = errorMessage(error);
      setConfigurationError(message);
      showNotice(message);
    } finally {
      setConfigurationBusy(false);
    }
  }

  async function handleSaveTheme(nextThemeId: ThemeId) {
    const previousThemeId = themeId;
    setThemeId(nextThemeId);
    setConfigurationBusy(true);
    setConfigurationError("");
    try {
      const settings = await updateThemeSetting(nextThemeId);
      setConfiguration((current) => current ? { ...current, settings } : current);
      setBootstrap((current) => current ? { ...current, settings } : current);
      showNotice(`Thème ${themeLabel(nextThemeId)} activé.`, "success");
    } catch (error) {
      setThemeId(previousThemeId);
      const message = errorMessage(error);
      setConfigurationError(message);
      showNotice(message);
    } finally {
      setConfigurationBusy(false);
    }
  }

  function selectSession(sessionId: string) {
    localStorage.setItem("nora.activeSessionId", sessionId);
    if (sessionId === activeSessionRef.current) {
      setSessionRailOpen(false);
      return;
    }
    setDetailLoading(true);
    activeSessionRef.current = sessionId;
    detailRef.current = null;
    setDetail(null);
    setActiveSessionId(sessionId);
    setSessionRailOpen(false);
  }

  async function handleCreate(input: Parameters<typeof createSession>[0]) {
    setCreateBusy(true);
    setCreateError("");
    try {
      const session = await createSession(input);
      setCreateOpen(false);
      await refreshBootstrap();
      selectSession(session.id);
      showNotice("Conversation créée.", "success");
    } catch (error) {
      setCreateError(errorMessage(error));
    } finally {
      setCreateBusy(false);
    }
  }

  async function handleCreateProject(input: Parameters<typeof createProject>[0]) {
    setCreateProjectBusy(true);
    setCreateProjectError("");
    try {
      const project = await createProject(input);
      setCreateProjectOpen(false);
      await refreshBootstrap();
      openProject(project.id);
      showNotice("Projet créé.", "success");
    } catch (error) {
      setCreateProjectError(errorMessage(error));
    } finally {
      setCreateProjectBusy(false);
    }
  }

  async function handleDelete(session: SessionSummary) {
    if (!window.confirm(`Supprimer la session « ${session.title} » ?`)) return;
    setDeletingSessionId(session.id);
    try {
      await deleteSession(session.id);
      await refreshBootstrap();
      showNotice("Conversation supprimée.", "success");
    } catch (error) {
      showNotice(errorMessage(error));
    } finally {
      setDeletingSessionId(null);
    }
  }

  async function handleSend(content: string, attachmentIds: string[] = []): Promise<boolean> {
    if (!activeSessionId) return false;
    setSending(true);
    try {
      await sendMessage(activeSessionId, content, attachmentIds);
      try {
        await Promise.all([refreshBootstrap(), refreshDetail(activeSessionId)]);
      } catch (error) {
        showNotice(`Message envoyé, mais l’actualisation a échoué : ${errorMessage(error)}`);
      }
      return true;
    } catch (error) {
      showNotice(errorMessage(error));
      return false;
    } finally {
      setSending(false);
    }
  }

  async function handleInteractionResponse(
    requestId: string,
    answers: InteractionAnswerInput[]
  ): Promise<boolean> {
    if (!activeSessionId) return false;
    setSending(true);
    try {
      await sendInteractionResponse(activeSessionId, requestId, answers);
      try {
        await Promise.all([refreshBootstrap(), refreshDetail(activeSessionId)]);
      } catch (error) {
        showNotice(`Réponse envoyée, mais l’actualisation a échoué : ${errorMessage(error)}`);
      }
      return true;
    } catch (error) {
      showNotice(errorMessage(error));
      return false;
    } finally {
      setSending(false);
    }
  }

  async function handleApprovalDecision(
    approvalId: string,
    decision: ApprovalDecision
  ): Promise<boolean> {
    try {
      await resolveApproval(approvalId, decision);
      await Promise.all([refreshBootstrap(), refreshDetail()]);
      showNotice(approvalDecisionNotice(decision), "success");
      return true;
    } catch (error) {
      showNotice(errorMessage(error));
      return false;
    }
  }

  async function handleCancel(runId: string) {
    try {
      await cancelRun(runId);
      await Promise.all([refreshBootstrap(), refreshDetail()]);
      showNotice("Arrêt de la réponse demandé.", "info");
    } catch (error) {
      showNotice(errorMessage(error));
    }
  }

  async function handleRetry(messageId: string) {
    if (!activeSessionId || sending) return;
    setSending(true);
    try {
      await retryMessage(messageId);
      await Promise.all([refreshBootstrap(), refreshDetail(activeSessionId)]);
      showNotice("Message relancé.", "info");
    } catch (error) {
      showNotice(errorMessage(error));
    } finally {
      setSending(false);
    }
  }

  if (authState !== "authenticated") {
    return <AuthGate busy={authState === "checking" || authBusy} error={authError} onLogin={handleLogin} />;
  }

  const sessions = bootstrap?.sessions ?? [];
  const projects = bootstrap?.projects ?? [];
  const recurringTasks = bootstrap?.recurringTasks ?? [];
  const pendingApprovalCount = sessions.reduce((total, session) => total + (session.pendingApprovalCount ?? 0), 0);
  const activeSession = sessions.find((session) => session.id === activeSessionId) ?? null;
  const generalSession = sessions.find((session) => session.isGeneral) ?? null;
  const activeProject = activeSession?.projectId
    ? projects.find((project) => project.id === activeSession.projectId) ?? null
    : null;
  const activeUsage = detail?.session.id === activeSessionId ? detail.usage : null;
  const runtimeAvailable = Boolean(status?.ok && status.worker && streamConnected);
  const runtimeTitle = headerRuntimeTitle(status, streamConnected);

  return (
    <div className="app-shell">
      <GlobalDrawer
        open={globalDrawerOpen}
        status={status}
        projectCount={projects.length}
        sessionCount={sessions.length}
        taskCount={recurringTasks.length}
        pendingApprovalCount={pendingApprovalCount}
        onClose={() => setGlobalDrawerOpen(false)}
        onOpenToday={openToday}
        onOpenSearch={openSearch}
        onOpenArtifacts={openArtifacts}
        onOpenProjects={openProjects}
        onOpenSessions={() => openSessions(null)}
        onOpenRecurringTasks={openRecurringTasks}
        onOpenMemory={() => openMemory(null)}
        onOpenFiles={() => openFiles(
          workspaceRelativePath(
            bootstrap?.workspaceRoot ?? "/projects",
            activeSession?.workspacePath ?? bootstrap?.workspaceRoot ?? "/projects"
          ),
          null,
          "directory"
        )}
        onOpenCapabilities={openCapabilities}
        onOpenConfiguration={openConfiguration}
        onLogout={handleLogout}
      />
      {projectsOpen ? <Suspense fallback={<PanelFallback />}><ProjectsPanel
        open
        projects={projects}
        onClose={() => setProjectsOpen(false)}
        onCreateProject={() => { setCreateProjectError(""); setCreateProjectOpen(true); }}
        onOpenProject={openProject}
        onOpenSessions={openSessions}
        onCreateSession={(projectId) => { setProjectsOpen(false); openCreateSession(projectId); }}
      /></Suspense> : null}
      {todayOpen ? <Suspense fallback={<PanelFallback />}><TodayPanel
        open
        refreshVersion={bootstrap?.latestSeq ?? 0}
        onClose={() => setTodayOpen(false)}
        onOpenProject={openProject}
        onOpenSession={(sessionId) => { setTodayOpen(false); selectSession(sessionId); }}
        onOpenRecurringTasks={openRecurringTasks}
        onOpenMemory={() => openMemory(null)}
        onNotify={showNotice}
      /></Suspense> : null}
      {searchOpen ? <Suspense fallback={<PanelFallback />}><SearchPanel
        open
        onClose={() => setSearchOpen(false)}
        onOpenProject={openProject}
        onOpenSession={(sessionId) => { setSearchOpen(false); selectSession(sessionId); }}
        onOpenMemory={(projectId) => openMemory(projectId)}
        onOpenFile={(path) => openFiles(path)}
        onNotify={showNotice}
      /></Suspense> : null}
      {artifactsOpen ? <Suspense fallback={<PanelFallback />}><ArtifactsPanel
        open
        projects={projects}
        refreshVersion={bootstrap?.latestSeq ?? 0}
        onClose={() => setArtifactsOpen(false)}
        onOpenFile={(path) => openFiles(path)}
        onNotify={showNotice}
      /></Suspense> : null}
      {recurringTasksOpen ? <Suspense fallback={<PanelFallback />}><RecurringTasksPanel
        open
        tasks={recurringTasks}
        projects={projects}
        refreshVersion={bootstrap?.latestSeq ?? 0}
        onClose={() => setRecurringTasksOpen(false)}
        onChanged={async () => { await refreshBootstrap(); }}
        onOpenSession={(sessionId) => { setRecurringTasksOpen(false); selectSession(sessionId); }}
        onNotify={showNotice}
      /></Suspense> : null}
      {projectDetailId ? <Suspense fallback={<PanelFallback />}><ProjectDetailPanel
        open
        projectId={projectDetailId}
        refreshVersion={bootstrap?.latestSeq ?? 0}
        onClose={() => setProjectDetailId(null)}
        onChanged={async () => { await refreshBootstrap(); }}
        onOpenSessions={(projectId) => openSessions(projectId)}
        onOpenSession={(sessionId) => { setProjectDetailId(null); selectSession(sessionId); }}
        onCreateSession={(projectId) => { setProjectDetailId(null); openCreateSession(projectId); }}
        onOpenMemory={(projectId) => openMemory(projectId)}
        onNotify={showNotice}
      /></Suspense> : null}
      <SessionRail
        open={sessionRailOpen}
        sessions={sessions}
        projects={projects}
        projectFilterId={sessionProjectFilterId}
        activeSessionId={activeSessionId}
        busySessionId={deletingSessionId}
        onClose={() => setSessionRailOpen(false)}
        onCreate={openCreateSession}
        onClearProjectFilter={() => setSessionProjectFilterId(null)}
        onSelect={selectSession}
        onDelete={handleDelete}
      />
      <main className="main-shell">
        <header className="topbar">
          <button className="icon-command topbar-menu-command" type="button" onClick={openGlobalDrawer} aria-label="Ouvrir le menu principal" title="Menu">
            <Menu aria-hidden="true" />
          </button>
          <span className="topbar-logo nora-logo" aria-hidden="true" />
          <div className="topbar-title">
            <strong>Nora</strong>
            <span className="topbar-subtitle">Assistante personnelle</span>
          </div>
          <div className="topbar-status">
            <button className="icon-command topbar-mobile-overflow-command" type="button" onClick={openSearch} aria-label="Rechercher" title="Recherche">
              <Search aria-hidden="true" />
            </button>
            <button className={`icon-command topbar-mobile-overflow-command ${pendingApprovalCount ? "attention" : ""}`} type="button" onClick={openToday} aria-label="Ouvrir Aujourd’hui" title="Aujourd’hui">
              <CalendarCheck2 aria-hidden="true" />
              {pendingApprovalCount ? <span className="command-attention-count">{pendingApprovalCount}</span> : null}
            </button>
            <button className="icon-command" type="button" onClick={() => openSessions(null)} aria-label="Ouvrir les conversations" title="Conversations">
              <MessagesSquare aria-hidden="true" />
            </button>
            <button
              className={`icon-command home-command ${activeSession?.isGeneral ? "active" : ""}`}
              type="button"
              onClick={() => { if (generalSession) selectSession(generalSession.id); }}
              disabled={!generalSession}
              aria-label="Ouvrir la conversation principale"
              aria-current={activeSession?.isGeneral ? "page" : undefined}
              title="Accueil"
            >
              <House aria-hidden="true" />
            </button>
          </div>
        </header>
        <ConversationContextBar
          session={activeSession}
          projectName={activeProject?.name ?? null}
          settings={bootstrap?.settings ?? null}
        />
        <StatusBar
          status={status}
          messageCount={detail?.messages.length ?? activeSession?.messageCount ?? 0}
          usage={activeUsage}
          onOpenUsage={openUsage}
        />
        {!sessions.length ? (
          <section className="first-session">
            <span className="nora-logo" aria-hidden="true" />
            <h1>Nora</h1>
            <button className="text-command primary" type="button" onClick={() => openCreateSession(null)}>
              <MessageSquarePlus aria-hidden="true" />
              Nouvelle session
            </button>
          </section>
        ) : (
          <ChatView
            detail={detail}
            loading={detailLoading}
            sending={sending}
            workspaceRoot={bootstrap?.workspaceRoot ?? "/projects"}
            runtimeAvailable={runtimeAvailable}
            runtimeTitle={runtimeTitle}
            onSend={handleSend}
            onRespond={handleInteractionResponse}
            onResolveApproval={handleApprovalDecision}
            onRetry={handleRetry}
            onCancel={handleCancel}
            onOpenMemory={() => openMemory(null)}
            onOpenFile={(path, line) => {
              const sourceSessionId = detail?.session.id;
              if (sourceSessionId) openFiles(path, line, "auto", sourceSessionId);
            }}
            onOpenSession={selectSession}
            onNotify={showNotice}
          />
        )}
      </main>
      {usageOpen ? <Suspense fallback={<PanelFallback />}><UsagePanel
        open
        sessionTitle={activeSession?.title ?? "Session"}
        usage={activeUsage}
        status={status}
        resetBusy={resetBusy}
        onSaveResetPolicy={saveCodexResetPolicy}
        onConsumeReset={useCodexReset}
        onClose={() => setUsageOpen(false)}
      /></Suspense> : null}
      {memoryOpen ? <Suspense fallback={<PanelFallback />}><MemoryPanel
        open
        projects={projects}
        activeSession={activeSession}
        projectFilterId={memoryProjectFilterId}
        onClose={() => { setMemoryOpen(false); setMemoryProjectFilterId(null); }}
        onChanged={async () => {
          await Promise.all([refreshBootstrap(), refreshDetail()]);
        }}
        onNotify={showNotice}
      /></Suspense> : null}
      {filesOpen ? <Suspense fallback={<PanelFallback />}><FileExplorerPanel open target={fileTarget} onClose={closeFiles} /></Suspense> : null}
      {capabilitiesOpen ? <Suspense fallback={<PanelFallback />}><CapabilitiesPanel
        open
        inventory={capabilities}
        projects={projects}
        selectedProjectId={capabilitiesProjectId}
        busyCapabilityId={capabilityPolicyBusyId}
        loading={capabilitiesLoading}
        error={capabilitiesError}
        onClose={() => setCapabilitiesOpen(false)}
        onRefresh={() => loadCapabilities(true, capabilitiesProjectId)}
        onSelectProject={selectCapabilitiesProject}
        onSavePolicy={saveCapabilityPolicy}
        onAddWithNora={askNoraToAddCapability}
      /></Suspense> : null}
      {configurationOpen ? <Suspense fallback={<PanelFallback />}><ConfigurationPanel
        open
        loading={configurationLoading}
        busy={configurationBusy}
        error={configurationError}
        configuration={configuration}
        session={activeSession}
        onClose={() => setConfigurationOpen(false)}
        onRefresh={() => loadConfiguration(true)}
        onSaveDefaults={handleSaveDefaults}
        onSaveTheme={handleSaveTheme}
        onSaveSession={handleSaveSessionSettings}
        onLogout={handleLogout}
      /></Suspense> : null}
      <CreateSessionDialog
        open={createOpen}
        busy={createBusy}
        error={createError}
        workspaceRoot={bootstrap?.settings.defaultWorkspacePath ?? bootstrap?.workspaceRoot ?? "/projects"}
        drivers={bootstrap?.drivers ?? []}
        projects={projects}
        initialProjectId={createSessionProjectId}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
      />
      <CreateProjectDialog
        open={createProjectOpen}
        busy={createProjectBusy}
        error={createProjectError}
        workspaceRoot={bootstrap?.workspaceRoot ?? "/projects"}
        onClose={() => setCreateProjectOpen(false)}
        onCreate={handleCreateProject}
      />
      <ToastViewport notices={notices} onDismiss={dismissNotice} />
    </div>
  );
}

function themeLabel(themeId: ThemeId): string {
  return getTheme(themeId).name;
}

function headerRuntimeTitle(status: StatusPayload | null, streamConnected: boolean): string {
  if (!status) return "État de Nora en cours de chargement.";
  if (status.ok && status.worker && streamConnected) return "Nora est en ligne.";
  return "Connexion Nora dégradée. Ouvrir le menu pour le détail.";
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Une erreur inattendue est survenue.";
}

function applyAssistantUpdate(detail: SessionDetail | null, event: EventRecord): SessionDetail | null {
  if (!detail || detail.session.id !== event.sessionId) return null;
  const messageId = typeof event.payload.messageId === "string" ? event.payload.messageId : "";
  const index = detail.messages.findIndex((message) => message.id === messageId);
  if (index < 0) return null;
  const current = detail.messages[index];
  if (!current) return null;

  let content: string;
  if (event.payload.replace === true && typeof event.payload.content === "string") {
    content = event.payload.content;
  } else if (typeof event.payload.delta === "string" && typeof event.payload.baseLength === "number") {
    if (current.content.length !== event.payload.baseLength) return null;
    content = current.content + event.payload.delta;
  } else return null;

  const messages = detail.messages.slice();
  messages[index] = { ...current, content, status: "draft", updatedAt: event.createdAt };
  return { ...detail, messages };
}

function applyLiveEvent(detail: SessionDetail | null, event: EventRecord): SessionDetail | null {
  if (!detail || detail.session.id !== event.sessionId) return null;
  if (detail.events.some((candidate) => candidate.id === event.id)) return detail;
  const runStatus = liveRunStatus(event.type);
  const runs = runStatus && event.runId
    ? detail.runs.map((run) => run.id === event.runId
      ? { ...run, status: runStatus, updatedAt: event.createdAt }
      : run)
    : detail.runs;
  const sessionStatus = runStatus === "completed" || runStatus === "cancelled"
    ? "idle"
    : runStatus === "failed" ? "failed" : runStatus ?? detail.session.status;
  return {
    ...detail,
    session: { ...detail.session, status: sessionStatus },
    runs,
    events: [...detail.events, event].slice(-120),
  };
}

function liveRunStatus(type: string): SessionDetail["runs"][number]["status"] | null {
  if (type === "run.queued") return "queued";
  if (type === "run.started" || type === "run.recovered") return "running";
  if (type === "run.cancellation_requested") return "cancelling";
  if (type === "run.completed") return "completed";
  if (type === "run.cancelled") return "cancelled";
  if (type === "run.failed") return "failed";
  return null;
}

function workspaceRelativePath(rootPath: string, workspacePath: string): string {
  const root = rootPath.replace(/\/+$/, "");
  const workspace = workspacePath.replace(/\/+$/, "");
  if (workspace === root) return "";
  return workspace.startsWith(`${root}/`) ? workspace.slice(root.length + 1) : "";
}

function recurringTaskNotification(event: EventRecord): { message: string; tone: ToastTone } | null {
  if (event.payload.notify !== true || !event.type.startsWith("recurring_task.")) return null;
  const name = typeof event.payload.name === "string" ? event.payload.name : "Tâche récurrente";
  if (event.type === "recurring_task.completed") return { message: `« ${name} » est terminée.`, tone: "success" };
  if (event.type === "recurring_task.failed") return { message: `« ${name} » a échoué.`, tone: "error" };
  if (event.type === "recurring_task.skipped") return { message: `« ${name} » n’a pas démarré car une occurrence est déjà active.`, tone: "info" };
  if (event.type === "recurring_task.cancelled") return { message: `« ${name} » a été annulée.`, tone: "info" };
  return null;
}

function shouldRefreshBootstrapForEvent(type: string): boolean {
  return type.startsWith("project.")
    || type.startsWith("recurring_task.")
    || type.startsWith("approval.")
    || type.startsWith("artifacts.")
    || [
      "session.created",
      "session.renamed",
      "session.deleted",
      "session.settings.updated",
      "message.created",
      "run.queued",
      "run.completed",
      "run.failed",
      "run.cancelled",
    ].includes(type);
}

function shouldRefreshDetailForEvent(type: string): boolean {
  return [
    "message.created",
    "assistant.completed",
    "interaction.requested",
    "interaction.responded",
    "approval.requested",
    "approval.resolved",
    "run.completed",
    "run.failed",
    "run.cancelled",
  ].includes(type);
}

function PanelFallback() {
  return <div className="panel-lazy-fallback" role="status">Chargement…</div>;
}
